import { trackAnalyticsEvent } from 'util/analytics';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useInfiniteLoader, useProfile } from 'hooks';
import { useSchoolsApi } from 'services/api';
import { ListEventsResponse } from 'services/api/apis/events/dto';
import { OrganizationSummary } from 'services/api/models';
import { CATEGORIES } from 'enum/categories';
import {
  COLORS,
  FONT,
  NoResultsState,
  ErrorState,
  FilterChipDescriptor,
  localNowIso,
  useOrgModal,
} from '../../shared';
import { canOrganizeFeedEvent } from '../rail/feedEvents';
import { EventGridCard, EventGridCardSkeleton } from './EventGridCard';
import { EventsFilterBar, EventScope } from './EventsFilterBar';

export interface EventsBrowseProps {
  slug: string | null;
}

const PAGE_SIZE = 24;
const GRID_GAP = 32;

const categoryOptions = CATEGORIES.map(c => ({ value: c, label: c }));

export const EventsBrowse = ({ slug }: EventsBrowseProps): ReactElement => {
  const router = useRouter();
  const { profile } = useProfile();
  const schoolsApi = useSchoolsApi();
  const { openOrg, orgModal } = useOrgModal();

  const [scope, setScope] = useState<EventScope>('all');
  const [category, setCategory] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const now = useMemo(() => localNowIso(), []);

  const fetchPage = useCallback(
    (
      schoolSlug: string,
      cursor: string | null,
      searchVal: string,
      categoryVal: string | null,
      scopeVal: EventScope,
    ) =>
      schoolsApi.getSchoolEvents(schoolSlug, {
        cursor: cursor ?? undefined,
        pageSize: PAGE_SIZE,
        search: searchVal || undefined,
        categories: categoryVal ? [categoryVal] : undefined,
        organization: scopeVal === 'following' ? 'followed' : undefined,
        timeFilter: 'upcoming',
        now,
      }),
    [schoolsApi, now],
  );

  const rEvents = useInfiniteLoader<
    ListEventsResponse,
    [string, string | null, string, string | null, EventScope]
  >((pageIndex, previousPageData) => {
    if (!slug) return null;
    if (pageIndex > 0 && !previousPageData?.cursor) return null;
    return [slug, previousPageData?.cursor ?? null, search, category, scope];
  }, fetchPage);

  const events = useMemo(
    () => rEvents.data?.flatMap(p => p?.events ?? []) ?? [],
    [rEvents.data],
  );

  const countKey = (organization?: 'followed') => [
    'events-count',
    slug,
    search,
    category,
    organization ?? 'all',
  ];

  const fetchCount = useCallback(
    async (organization?: 'followed'): Promise<number | undefined> => {
      if (!slug) return undefined;
      const res = await schoolsApi.getSchoolEvents(slug, {
        pageSize: 1,
        search: search || undefined,
        categories: category ? [category] : undefined,
        organization,
        timeFilter: 'upcoming',
        now,
      });
      return res.success ? res.value.count : undefined;
    },
    [schoolsApi, slug, search, category, now],
  );

  const listCount = rEvents.data?.[0]?.count;
  const otherCountKey =
    scope === 'all' ? countKey('followed') : countKey();
  const { data: otherCount } = useSWR<number | undefined>(
    slug ? otherCountKey : null,
    () => fetchCount(scope === 'all' ? 'followed' : undefined),
  );
  const allCount = scope === 'all' ? listCount : otherCount;
  const followingCount = scope === 'following' ? listCount : otherCount;
  const totalCount = scope === 'following' ? followingCount : allCount;
  const isInitialLoading = !rEvents.data && rEvents.isValidating;
  const isError = Boolean(rEvents.error);
  const hasFilters =
    search.trim() !== '' || category !== null || scope === 'following';

  const goToEvent = (url: string): void => {
    trackAnalyticsEvent({ name: 'recommended_events_row_clicked' });
    void router.push(url);
  };

  const activeChips = useMemo<FilterChipDescriptor[]>(() => {
    const chips: FilterChipDescriptor[] = [];
    if (scope === 'following') {
      chips.push({
        id: 'scope',
        label: 'Following',
        onRemove: () => setScope('all'),
      });
    }
    if (category) {
      chips.push({
        id: 'category',
        label: category,
        onRemove: () => setCategory(null),
      });
    }
    if (search.trim()) {
      chips.push({
        id: 'search',
        label: `"${search.trim()}"`,
        onRemove: () => setSearch(''),
      });
    }
    return chips;
  }, [scope, category, search]);

  const clearAll = (): void => {
    setScope('all');
    setCategory(null);
    setSearch('');
  };

  const renderBody = (): ReactElement => {
    if (isInitialLoading) {
      return (
        <Box sx={gridSx}>
          {Array.from({ length: 8 }).map((_, i) => (
            <EventGridCardSkeleton key={i} />
          ))}
        </Box>
      );
    }

    if (isError) {
      return (
        <ErrorState
          title="Couldn't load events."
          body="Something went wrong fetching campus events. Check your connection and try again."
          onRetry={() => void rEvents.mutate()}
        />
      );
    }

    if (events.length === 0) {
      return hasFilters ? (
        <NoResultsState
          title="No events match your filters."
          body="Try a different category, clear your search, or switch back to all events."
          chips={activeChips}
          clearLabel="Clear all filters"
          onClear={clearAll}
        />
      ) : (
        <NoResultsState
          icon="event_busy"
          title="No upcoming events yet."
          body="Nothing on the schedule right now — check back soon for new things to do on campus."
        />
      );
    }

    return (
      <>
        <Box sx={gridSx}>
          {events.map(event => (
            <EventGridCard
              key={event.id}
              event={event}
              reserved={event.reserved}
              checkedIn={event.checkedIn}
              checkInLabel={
                canOrganizeFeedEvent(event, profile)
                  ? 'Check in attendees'
                  : 'Check in'
              }
              onCheckIn={() => goToEvent(event.url)}
              onDetails={() => goToEvent(event.url)}
              onEventClick={() => goToEvent(event.url)}
              onOrgClick={(org: OrganizationSummary) =>
                openOrg({
                  id: org.id,
                  name: org.name,
                  logoUrl: org.logoUrl,
                  // Event host payloads often omit org.school — fall back to page slug.
                  schoolSlug: org.school?.slug ?? slug ?? undefined,
                })
              }
            />
          ))}
        </Box>
        {rEvents.isValidating && (
          <Box display="flex" justifyContent="center" py="24px" width="100%">
            <CircularProgress size={28} sx={{ color: COLORS.navy }} />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
      }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: { xs: '24px', md: '28px' },
          lineHeight: '36px',
          color: COLORS.headingDark,
          m: 0,
        }}
      >
        Discover Upcoming Events
      </Typography>

      <EventsFilterBar
        scope={scope}
        onScopeChange={setScope}
        allCount={allCount}
        followingCount={followingCount}
        category={category}
        onCategoryChange={setCategory}
        categoryOptions={categoryOptions}
        search={search}
        onSearchChange={setSearch}
        resultCount={totalCount}
      />

      {renderBody()}
      {orgModal}
    </Box>
  );
};

const CARD_WIDTH = 280;

const gridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: `repeat(auto-fill, minmax(min(100%, ${CARD_WIDTH}px), ${CARD_WIDTH}px))`,
  },
  gap: `${GRID_GAP}px`,
  width: '100%',
} as const;
